import { format } from 'date-fns';
import { Pencil, Trash2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Transaction } from '@/hooks/useTransactions';

interface TransactionMobileCardProps {
  transaction: Transaction;
  displayCurrency?: string;
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
  onClick?: (transaction: Transaction) => void;
}

export function TransactionMobileCard({
  transaction,
  onEdit,
  onDelete,
  onClick,
}: TransactionMobileCardProps) {
  const isIncome = transaction.type === 'income';

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const categoryLabel = transaction.sub_categories
    ? `${transaction.sub_categories.icon ?? ''} ${transaction.sub_categories.name}`.trim()
    : transaction.categories
    ? `${transaction.categories.icon ?? ''} ${transaction.categories.name}`.trim()
    : 'Uncategorized';

  return (
    <Card
      className={cn('w-full', onClick && 'cursor-pointer')}
      onClick={onClick ? () => onClick(transaction) : undefined}
    >
      <CardContent className="p-4 space-y-2">
        {/* Top row: date + amount */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {format(new Date(transaction.transaction_date), 'MMM d, yyyy')}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 font-mono text-sm font-semibold',
              isIncome ? 'text-emerald-600' : 'text-red-600'
            )}
          >
            {isIncome ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {formatCurrency(Number(transaction.amount), transaction.currency)}
          </span>
        </div>

        {/* Middle row: description + category badge */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm truncate">
            {transaction.description || '—'}
          </span>
          <Badge variant="secondary" className="shrink-0 text-xs">
            {categoryLabel}
          </Badge>
        </div>

        {/* Bottom row: account name + edit/delete buttons */}
        <div
          className="flex items-center justify-between"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-sm text-muted-foreground">
            {transaction.accounts?.name ?? '—'}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onEdit(transaction)}
              aria-label="Edit transaction"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onDelete(transaction.id)}
              aria-label="Delete transaction"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
